
import { useState, useEffect } from 'react'
import DatePicker from "react-datepicker";

import { ResponsiveContainer } from '../ResponsiveContainer';

import { CourseSKUPicker } from './CourseSKUPicker';

import { formatDateForBirth } from "../../dates";
import { SKU_TAG_COUNT } from "../../constants";

import { useNavigate } from "react-router-dom";

import {
  Dropdown,
  Container,
  Header,
  Button,
  Segment,
  Form,
  FormInput,
} from 'semantic-ui-react'

import { gql, useQuery, useMutation } from '@apollo/client';

const GET_POSSIBLE_STUDENTS = gql`
    query AdminGetAllUsers {
        adminGetAllUsers {
            name
            identifier
            id
        }
    }
`;

interface DayAndTime {
    day: number;
    checked: boolean;
    time: string;
    label: string;
}

const makeDay = (index: number): DayAndTime => ({
    day: index,
    checked: false,
    time: "",
    label: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][index]
});

const CREATE_COURSE = gql`
    mutation CreateCourse($name: String!, $sku: String!, $sessionCount: String!, $daysAndTimes: [String!]!, $studentIds: [Int!]!, $tagIds: [Int!]!, $durationMinutes: Int!, $startDate: String!) {
        createCourse(name: $name, SKU: $sku, sessionCount: $sessionCount, daysAndTimes: $daysAndTimes, studentIds: $studentIds, tagIds: $tagIds, durationMinutes: $durationMinutes, startDate: $startDate) {
            status
            id
        }
    }
`;

const NewCourse = () => {
    let navigate = useNavigate();

    const [courseName, setCourseName] = useState("");
    const [sku, setSku] = useState("XX-XX-XX-XX-XX-XX-XX-XX");
    const [sessionCount, setSessionCount] = useState(10);
    // "daysAndTimes": ["1_10:00", "2_09:00"],
    const [daysAndTimes, setDaysAndTimes] = useState([0,1,2,3,4,5,6].map(makeDay));
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [graphqlStartDate, setGraphqlStartDate] = useState<string | "">("");
    const [durationMinutes, setDurationMinutes] = useState(60);
    const [tagIds, setTagIds] = useState<number[]>([]);
    const [studentIds, setStudentIds] = useState<number[]>([]);

    const [showErrors, setShowErrors] = useState(false);

    // GET possible students
    const { loading, error, data } = useQuery(GET_POSSIBLE_STUDENTS);

    // mutation
    const [
        createCourse,
        { data: createCourseData }
    ] = useMutation(CREATE_COURSE);

    // if craeteCourseHasData then navigate
    useEffect(() => {
        if (createCourseData) {
            navigate(`/courses/${createCourseData.createCourse.id}`);
        }
    }, [createCourseData, navigate]);

    useEffect(() => {
        setGraphqlStartDate(formatDateForBirth(new Date().getTime()));
    }, [setGraphqlStartDate]);

    const handleSave = () => {
        setShowErrors(true);

        if (
            courseName.length < 1
            || sku.length < 1
            || graphqlStartDate === ""
            || sessionCount < 1
            || isNaN(sessionCount)
            || daysAndTimes.filter((d) => d.checked && (d.time.length === 4 || d.time.length === 5)).length === 0
            || durationMinutes < 1
            || isNaN(durationMinutes)
            || tagIds.length < SKU_TAG_COUNT // this represents when a sku value has not been picked
        ) {
            return;
        }

        createCourse({
            variables: {
                name: courseName,
                sku,
                sessionCount: sessionCount.toString(),
                daysAndTimes: daysAndTimes.filter((d) => d.checked).map((d) => `${d.day}_${d.time}`),
                studentIds,
                tagIds,
                durationMinutes,
                startDate: graphqlStartDate,
            }
        });
    }
  return (
    <ResponsiveContainer>
      <Segment style={{ padding: '2em 0em' }} vertical>
        <Container>
            <Header as='h1'>New course</Header>
            <Form>
                <FormInput
                    error={showErrors && courseName.length < 1 && 'Please enter the name'}
                    fluid
                    label='Name'
                    placeholder='Name'
                    value={courseName}
                    onChange={(e) => setCourseName(e.target.value)}
                />
                SKU: {sku}
                <CourseSKUPicker showErrors={showErrors} setTagIds={setTagIds} setSku={setSku}/>
                <FormInput
                    fluid
                    error={showErrors && startDate === null && 'Please enter the day of the first class'}
                    label='Start date'
                >
                    <DatePicker
                        selected={startDate}
                        dateFormat="dd/MM/yyyy"
                        onChange={(date) => {
                            date?.setHours(0, 0, 0, 0);
                            console.log(date)
                            console.log(date?.getTime())
                            setGraphqlStartDate(formatDateForBirth(date?.getTime() || 0))
                            setStartDate(date)
                        }}
                        showYearDropdown
                    />
                </FormInput>
                <Header as='h5'>Session times</Header>
                {daysAndTimes.map((dayAndTime, index) => {
                    const { label, checked, time } =  dayAndTime;

                return (<>
                    <Form.Checkbox
                        checked={checked}
                        label={label}
                        onChange={() => {
                            const newDaysAndTimes = [...daysAndTimes];
                            if (checked) {
                                // if toggling off, remove the time string
                                newDaysAndTimes[index].time = "";
                            }
                            newDaysAndTimes[index].checked = !checked;
                            setDaysAndTimes(newDaysAndTimes);
                        }}
                    />
                    {checked && <FormInput
                                    autoFocus
                                    error={showErrors && !/^[0-9]{1,2}:[0-9]{2}$/.test(time) && 'Please enter a valid time'}
                                    placeholder='HH:MM'
                                    value={time}
                                    onChange={(e) => {
                                        if (
                                            e.target.value.split(':').length > 2 // ensure there is only 1 colon in the value
                                        || e.target.value.length > 5 // ensure the total length of the value is less than 6
                                        || !/^[0-9:]*$/.test(e.target.value) // ensure new value is only made of numbers and colons
                                        ) {
                                            return;
                                        }

                                        // if a colon is the first character, add a 0
                                        if (e.target.value.length === 1 && e.target.value === ':') {
                                            e.target.value = '0:';
                                        }
                                        // if there are 3 numbers in a row, insert a colon
                                        if (e.target.value.length === 3 && (e.target.value[2] !== ':' && e.target.value[1] !== ':')) {
                                            e.target.value = `${e.target.value.slice(0, 2)}:${e.target.value.slice(2)}`;
                                        }

                                        // if there are 3 numbers after the colon move the colon
                                        if (e.target.value.length === 5 && e.target.value[2] !== ':' && e.target.value[3] !== ':' && e.target.value[4] !== ':') {
                                            // remove the first colon in the second position and put a colon in the middle
                                            const numbers = e.target.value.split(':').join('');
                                            e.target.value = `${numbers.slice(0, 2)}:${numbers.slice(2)}`;
                                        }

                                        // ensure the last two numbers are less than 60
                                        if (e.target.value.length > 3) {
                                            const [hours, minutes] = e.target.value.split(':');
                                            if (parseInt(minutes, 10) > 59) {
                                                e.target.value = hours + ':59';
                                            }
                                        }

                                        // if the first two digits are numbers and greater than 23 module them
                                        if (e.target.value.length > 2 && !e.target.value.slice(0,2).includes(":") && e.target.value.slice(0, 2) > '23') {
                                            e.target.value = '23' + e.target.value.slice(2);
                                        }


                                        const newDaysAndTimes = [...daysAndTimes];
                                        newDaysAndTimes[index].time = e.target.value;
                                        setDaysAndTimes(newDaysAndTimes);
                                    }}
                                />}
                    </>
                )})}
                <FormInput
                    error={showErrors && (sessionCount < 1 || isNaN(sessionCount)) && 'Please enter the number of sessions'}
                    fluid
                    label='Number of sessions'
                    placeholder='Number of sessions'
                    pattern="[0-9]*"
                    value={sessionCount === 0 || isNaN(sessionCount) ? '' : sessionCount}
                    onChange={(e) => {
                        setSessionCount(parseInt(e.target.value))
                    }}
                />
                <FormInput
                    error={showErrors && (durationMinutes < 1 || isNaN(durationMinutes)) && 'Please enter the duration of the class'}
                    fluid
                    label='Duration of the class'
                    placeholder='Duration of the class'
                    pattern="[0-9]*"
                    value={durationMinutes === 0 || isNaN(durationMinutes) ? '' : durationMinutes}
                    onChange={(e) => {
                        setDurationMinutes(parseInt(e.target.value))
                    }}
                />
                <FormInput
                    fluid
                    label='Students'
                >
                    {!loading && !error && data && data.adminGetAllUsers &&
                        <Dropdown
                            placeholder='Students'
                            fluid
                            multiple
                            value={studentIds}
                            onChange={(_, { value }) => setStudentIds(value as number[])}
                            search
                            selection
                            options={
                                data.adminGetAllUsers.map((p: any) => ({
                                    key: p.id,
                                    text: `${p.name} (${p.identifier})`,
                                    value: p.id,
                                }))
                            }
                        />
                    }
                </FormInput>
                {/* <FormInput
                    fluid
                    label='Course tags'
                    >
                    <TagsPicker
                    tags={tagIds.map((id) => ({ id }))}
                    refetchParent={() => {}}
                    loading={loadingTags}
                    error={errorTags}
                    data={dataTags}
                    updateTags={(tags: any) => {
                    setTagIds(tags.variables.tagIds)
                    }}
                    payload={{}}
                    />
                    </FormInput> */}
                <Button onClick={() => handleSave()}>Create</Button>
            </Form>
        </Container>
      </Segment>
    </ResponsiveContainer>
  )
}

export { NewCourse }
